import type { UpdatedProductKey } from '@cartwhl/common/stores/updatedProductStore';

export type PricingTabSlug =
  | 'individual-plans'
  | 'team-plans'
  | 'enterprise-plans';

export type BillingMode = 'monthly' | 'yearly';
export type PlanAccent = 'neutral' | 'blue' | 'green';

export interface PlanCardContent {
  productKey: UpdatedProductKey;
  title: string;
  description: string;
  credits: string;
  creditsPeriod: string;
  periodLabel: Record<BillingMode, string>;
  accent: PlanAccent;
}

export interface ComparisonValue {
  label: string;
  muted?: boolean;
  locked?: boolean;
  desktopLineBreak?: string;
}

export interface ComparisonSection {
  label: string;
  highlighted?: boolean;
  values: ComparisonValue[][];
}

export interface PlatformContent {
  title: string;
  description: string;
  features: { title: string; body: string }[];
  details: ComparisonSection[];
}

export interface PricingTabContent {
  slug: PricingTabSlug;
  label: string;
  shortLabel: string;
  cards?: PlanCardContent[];
  columns?: { label: string; accent: PlanAccent }[];
  comparisonTitle?: string;
  comparison?: ComparisonSection[];
  platform?: PlatformContent;
}

const apps = [
  'Performance Capture',
  'Performance Generation',
  'Performance Editor',
  'Mascot',
  'Character Generation',
  'Motion Library',
  'Cartographer',
];

const collaboration = [
  'Pooled credits',
  'Team library',
  'Team management controls',
];

const scaleSupport = [
  'Dedicated account manager',
  'White-glove onboarding',
  '12-hr SLA',
  '99.5% uptime SLA',
  'Dedicated Chat channel',
  'Quarterly check-ins',
  'Early beta access to all features',
];

const lockedApi: ComparisonValue[] = [
  { label: 'Team Pro & Scale only', muted: true, locked: true },
];

export const pricingTabs: PricingTabContent[] = [
  {
    slug: 'individual-plans',
    label: 'Individual plans',
    shortLabel: 'Individuals',
    cards: [
      {
        productKey: 'individual_basic',
        title: 'Individual Basic',
        description:
          'For solo creators that produce content in Canvas and through our suite of apps',
        credits: '200 credits',
        creditsPeriod: 'per month',
        periodLabel: { monthly: '/mo', yearly: '/mo' },
        accent: 'neutral',
      },
      {
        productKey: 'individual_pro',
        title: 'Individual Pro',
        description:
          'For creators who want more credits, plugin access, and API access for automation and programmatic workflows',
        credits: '500 credits',
        creditsPeriod: 'per month',
        periodLabel: { monthly: '/mo', yearly: '/mo' },
        accent: 'blue',
      },
    ],
    columns: [
      { label: 'Basic', accent: 'neutral' },
      { label: 'Pro', accent: 'blue' },
    ],
    comparisonTitle: 'Individual plan details',
    comparison: [
      {
        label: 'Apps',
        values: [
          apps.map((label) => ({ label })),
          apps.map((label) => ({ label })),
        ],
      },
      {
        label: 'Collaboration tools',
        values: [
          [{ label: 'Team plans only', muted: true, locked: true }],
          [{ label: 'Team plans only', muted: true, locked: true }],
        ],
      },
      {
        label: 'API',
        highlighted: true,
        values: [
          [{ label: 'Pro & Scale only', muted: true, locked: true }],
          [
            { label: 'Generate in batches' },
            { label: 'Automate pipelines' },
            { label: 'Scalable systems' },
            { label: 'Integrations' },
            { label: '1 concurrent batch generation' },
            { label: '30 motions max per batch' },
          ],
        ],
      },
      {
        label: 'Support',
        values: [
          [{ label: 'Email support' }, { label: '48hr SLA' }],
          [{ label: 'Email support' }, { label: '48hr SLA' }],
        ],
      },
    ],
  },
  {
    slug: 'team-plans',
    label: 'Team plans',
    shortLabel: 'Teams',
    cards: [
      {
        productKey: 'team_standard',
        title: 'Team Standard',
        description:
          'For small teams managing shared usage and scaling generation across members',
        credits: '500 credits',
        creditsPeriod: 'per seat pooled monthly',
        periodLabel: { monthly: 'per mo/seat', yearly: 'mo/seat' },
        accent: 'neutral',
      },
      {
        productKey: 'team_pro',
        title: 'Team Pro',
        description:
          'For teams running production pipelines with API-driven workflows, automation, and scalable generation',
        credits: '1500 credits',
        creditsPeriod: 'per seat pooled monthly',
        periodLabel: { monthly: 'per mo/seat', yearly: 'mo/seat' },
        accent: 'blue',
      },
      {
        productKey: 'team_scale',
        title: 'Team Scale',
        description:
          'For high-volume teams optimizing for speed, efficiency, and cost at scale',
        credits: '4000 credits',
        creditsPeriod: 'per seat pooled monthly',
        periodLabel: { monthly: 'per mo/seat', yearly: 'mo/seat' },
        accent: 'green',
      },
    ],
    columns: [
      { label: 'Standard', accent: 'neutral' },
      { label: 'Pro', accent: 'blue' },
      { label: 'Scale', accent: 'green' },
    ],
    comparisonTitle: 'Team plan details',
    comparison: [
      {
        label: 'Apps',
        values: [
          apps.map((label) => ({ label })),
          apps.map((label) => ({ label })),
          apps.map((label) => ({ label })),
        ],
      },
      {
        label: 'Collaboration tools',
        values: [
          collaboration.map((label) => ({ label })),
          collaboration.map((label) => ({ label })),
          collaboration.map((label) => ({ label })),
        ],
      },
      {
        label: 'API',
        highlighted: true,
        values: [
          lockedApi,
          [
            { label: 'Generate in batches' },
            { label: 'Automate pipelines' },
            { label: 'Scalable systems' },
            { label: 'Integrations' },
            {
              label: '3 concurrent batch generations per seat',
              desktopLineBreak: '3 concurrent batch\ngenerations per seat',
            },
            { label: '50 motions max per batch' },
          ],
          [
            { label: 'Generate in batches' },
            { label: 'Automate pipelines' },
            { label: 'Scalable systems' },
            { label: 'Integrations' },
            {
              label: '10 concurrent batch generations per seat',
              desktopLineBreak: '10 concurrent batch\ngenerations per seat',
            },
            { label: '100 motions max per batch' },
          ],
        ],
      },
      {
        label: 'Support',
        values: [
          [
            { label: 'Email & chat support' },
            { label: '(48hr SLA)' },
            { label: 'Team management controls' },
          ],
          [
            { label: 'Email & chat support' },
            { label: '(48hr SLA)' },
            { label: 'Team management controls' },
          ],
          scaleSupport.map((label) => ({ label })),
        ],
      },
    ],
  },
  {
    slug: 'enterprise-plans',
    label: 'Enterprise plans',
    shortLabel: 'Enterprise',
    platform: {
      title: 'Enterprise',
      description:
        'Unique plans for product integrations and large organizations. Custom features like, SSO integration, custom data management, and personalized models or datasets.',
      features: [
        {
          title: 'For your apps & games',
          body: 'Generate and stream character motion natively inside your app or game, giving your characters unique style while allowing your users to have their own custom motions.',
        },
        {
          title: 'Expand your systems toolset',
          body: 'Add generative motion as a native capability in your tools, giving your team the ability to animate without switching context.',
        },
      ],
      details: [
      ],
    },
  },
];

export interface CreditCostSubrow {
  label: string;
  value: string;
}

export interface CreditCostRow {
  label: string;
  subtitle?: string;
  value?: string;
  subrows?: CreditCostSubrow[];
}

export const creditCostRows: CreditCostRow[] = [
  {
    label: 'Performance Generation',
    subrows: [
      { label: 'Swing Sketch', value: '1 credit / sec' },
      { label: 'Swing Choreo (1–2 people)', value: '3 credits / sec' },
      { label: 'Swing Choreo (3–4 people)', value: '4 credits / sec' },
    ],
  },
  {
    label: 'Performance Capture',
    subrows: [
      { label: 'Motion capture', value: '1 credit / person / sec' },
      { label: 'Facial capture', value: '1 credit / sec / 2 faces' },
    ],
  },
  {
    label: 'Performance Edits',
    value: '0.5 credits / regenerated sec',
  },
  { label: 'Mascot generation', value: '9 credits / sec input video' },
  { label: 'Character generation', value: '60 credits / character' },
  { label: 'Character upload', value: 'Free' },
  { label: 'Cartographer Retargeting', value: 'Free' },
  { label: 'Motion export', value: 'Free' },
];
